import React from 'react';
import { Link } from 'react-router-dom';
import {
    FaHome, FaChevronRight, FaArrowRight, FaPhone, FaCheckCircle,
    FaHardHat, FaClipboardCheck, FaServer, FaShieldAlt,
    FaProjectDiagram, FaTools, FaRocket, FaLayerGroup, FaFileAlt
} from 'react-icons/fa';
import Navigation from '../components/Navigation';
import TopBar from '../components/TopBar';
import Footer from '../components/Footer';

const DataCenterCommissioningPage = () => {
    const phases = [
        {
            icon: <FaHardHat size={32} />,
            title: 'Construction Management',
            description: 'Expert oversight of every phase of data center construction — from groundbreaking to structural completion — ensuring safety, quality, and schedule compliance.',
            gradient: 'from-blue-600 to-indigo-600'
        },
        {
            icon: <FaLayerGroup size={32} />,
            title: 'L1–L5 Commissioning',
            description: 'Full-spectrum commissioning from component-level verification (L1) through integrated facility testing (L5), validating all mechanical, electrical, and controls systems.',
            gradient: 'from-cyan-500 to-blue-600'
        },
        {
            icon: <FaClipboardCheck size={32} />,
            title: 'Integrated Systems Testing (IST)',
            description: 'Rigorous IST protocols validating interdependencies between all critical systems — power, cooling, fire suppression, and monitoring — under real-world conditions.',
            gradient: 'from-violet-600 to-purple-700'
        },
        {
            icon: <FaRocket size={32} />,
            title: 'Ready for Service (RFS)',
            description: 'Comprehensive RFS validation ensuring all systems are fully operational, documented, and meet client acceptance criteria before handover.',
            gradient: 'from-green-500 to-emerald-600'
        },
        {
            icon: <FaFileAlt size={32} />,
            title: 'TCO Support',
            description: 'End-to-end Temporary Certificate of Occupancy management — coordinating inspections, documentation, and authority approvals for a smooth regulatory close-out.',
            gradient: 'from-orange-500 to-amber-600'
        },
        {
            icon: <FaProjectDiagram size={32} />,
            title: 'Project Lifecycle Execution',
            description: 'Unified project management across all phases — scheduling, risk mitigation, stakeholder communication, and quality assurance from inception to operational handover.',
            gradient: 'from-teal-500 to-cyan-600'
        },
        {
            icon: <FaServer size={32} />,
            title: 'Operational Readiness',
            description: 'Pre-operations planning, runbook development, staff readiness training, and day-one operational support to ensure seamless transition to live operations.',
            gradient: 'from-rose-500 to-pink-600'
        },
        {
            icon: <FaTools size={32} />,
            title: 'Quality & Compliance Oversight',
            description: 'Continuous quality assurance processes aligned with Uptime Institute, TIA-942, and ASHRAE standards, ensuring regulatory compliance throughout construction.',
            gradient: 'from-indigo-500 to-blue-700'
        },
        {
            icon: <FaShieldAlt size={32} />,
            title: 'Safety Management',
            description: 'Dedicated safety officers enforcing OSHA compliance, site safety plans, incident prevention protocols, and hazard analysis at every project stage.',
            gradient: 'from-red-500 to-rose-600'
        }
    ];

    const benefits = [
        'Full Lifecycle Project Management',
        'L1–L5 Commissioning Expertise',
        'On-Time, On-Budget Delivery',
        'OSHA & Regulatory Compliance',
        'Uptime Institute Standards Alignment',
        'IST & RFS Validation Support',
        'TCO Coordination & Approvals',
        '24/7 On-Site Project Presence'
    ];

    const stats = [
        { value: '106+', label: 'Projects Delivered' },
        { value: '12+', label: 'Years Experience' },
        { value: '97.2%', label: 'Uptime Achieved' },
        { value: '115+', label: 'Service Points' }
    ];

    return (
        <div className="font-sans">
            <TopBar />
            <Navigation />

            {/* Hero Section */}
            <section className="relative bg-gradient-to-br from-gray-900 via-blue-950 to-gray-900 py-24 overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}></div>
                </div>
                <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl opacity-20 translate-x-1/2 -translate-y-1/2"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-500 rounded-full filter blur-3xl opacity-20 -translate-x-1/2 translate-y-1/2"></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <nav className="flex items-center gap-2 text-sm text-gray-300 mb-8">
                        <Link to="/" className="flex items-center gap-1 hover:text-white transition-colors">
                            <FaHome size={14} />
                            <span>Home</span>
                        </Link>
                        <FaChevronRight size={10} className="text-gray-500" />
                        <Link to="/services" className="hover:text-white transition-colors">Services</Link>
                        <FaChevronRight size={10} className="text-gray-500" />
                        <span className="text-cyan-400">Data Center Commissioning</span>
                    </nav>

                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <div className="inline-block mb-4 px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full">
                                <span className="text-cyan-400 text-sm font-bold tracking-wider">MISSION-CRITICAL INFRASTRUCTURE</span>
                            </div>
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                                End-to-End <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">Data Center</span> Construction & Commissioning
                            </h1>
                            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                                Experienced construction and commissioning professionals managing every phase of mission-critical data center projects — from initial build through final operational readiness.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-blue-500/30 transition-all duration-300">
                                    Request a Consultation
                                    <FaArrowRight size={14} />
                                </Link>
                                <a href="tel:+12014099845" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white rounded-xl font-semibold hover:bg-white/10 transition-all duration-300">
                                    <FaPhone size={14} />
                                    Call Us Now
                                </a>
                            </div>
                        </div>
                        <div className="relative">
                            <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 backdrop-blur-sm rounded-2xl p-4 border border-white/10">
                                <img
                                    src="/serviceImages/dc-construction/dc-commissioning-hero.jpeg"
                                    alt="Data Center Construction & Commissioning"
                                    className="w-full h-auto rounded-xl shadow-2xl object-cover"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 pt-10 border-t border-white/10">
                        {stats.map((stat, index) => (
                            <div key={index} className="text-center">
                                <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">{stat.value}</div>
                                <div className="text-gray-400 text-sm mt-1">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* About Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-3xl p-12 border border-blue-100">
                        <div className="grid lg:grid-cols-2 gap-10 items-center">
                            <div>
                                <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">Who We Are</span>
                                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Built for Mission-Critical Precision</h2>
                                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                                    We provide experienced construction and commissioning professionals to manage and oversee every phase of mission-critical data center projects — from initial construction through final operational readiness.
                                </p>
                                <p className="text-lg text-gray-700 leading-relaxed">
                                    Our team specializes in full lifecycle project execution, including construction management, commissioning oversight, Integrated Systems Testing (IST), Ready for Service (RFS), and Temporary Certificate of Occupancy (TCO) support.
                                </p>
                            </div>
                            <div>
                                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                                    We help clients deliver projects safely, efficiently, and on schedule while ensuring operational reliability and compliance at every stage. We understand the complexity and precision required to deliver modern data center facilities.
                                </p>
                                <p className="text-lg text-gray-700 leading-relaxed">
                                    Our experienced professionals help ensure your project is completed safely, efficiently, and fully operational from day one. From L1 through L5 commissioning, IST validation, RFS readiness, and TCO support — we provide the expertise needed to successfully deliver mission-critical infrastructure projects.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services / Phases Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">Our Services</span>
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Comprehensive Construction & Commissioning Services</h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            End-to-end expertise across every phase of your data center project lifecycle
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {phases.map((phase, index) => (
                            <div
                                key={index}
                                className="group relative bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-2xl hover:shadow-blue-500/10 transition-all duration-500 hover:-translate-y-2"
                            >
                                <div className="p-8">
                                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${phase.gradient} flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300`}>
                                        {phase.icon}
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                                        {phase.title}
                                    </h3>
                                    <p className="text-gray-600 leading-relaxed mb-6">
                                        {phase.description}
                                    </p>
                                    <Link
                                        to="/contact"
                                        className="inline-flex items-center gap-2 text-blue-600 font-semibold group-hover:text-cyan-600 transition-colors"
                                    >
                                        Learn More
                                        <FaArrowRight className="group-hover:translate-x-2 transition-transform" size={12} />
                                    </Link>
                                </div>
                                <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${phase.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`}></div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div className="grid grid-cols-2 gap-3">
                            <img src="/serviceImages/dc-construction/construction-site.jpg" alt="Construction Site" className="w-full h-48 object-cover rounded-2xl shadow-lg col-span-2" />
                            <img src="/serviceImages/dc-construction/electrical-panels.jpg" alt="Electrical Panels" className="w-full h-36 object-cover rounded-xl shadow-lg" />
                            <img src="/serviceImages/dc-construction/server-room.jpg" alt="Server Room" className="w-full h-36 object-cover rounded-xl shadow-lg" />
                        </div>
                        <div>
                            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">Why Choose Us</span>
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">Why Partner With Us for Your Data Center Build</h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {benefits.map((benefit, index) => (
                                    <div key={index} className="flex items-start gap-3">
                                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center text-white flex-shrink-0 mt-0.5">
                                            <FaCheckCircle size={14} />
                                        </div>
                                        <span className="text-gray-700 font-medium">{benefit}</span>
                                    </div>
                                ))}
                            </div>
                            <Link
                                to="/contact"
                                className="inline-flex items-center gap-2 mt-10 px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-blue-500/30 transition-all duration-300"
                            >
                                Start Your Project
                                <FaArrowRight size={14} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="relative bg-gradient-to-r from-blue-700 to-cyan-600 py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}></div>
                </div>
                <div className="max-w-4xl mx-auto text-center relative z-10">
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">Ready to Build Your Next Data Center?</h2>
                    <p className="text-xl text-white/90 mb-12">
                        Let our expert team guide your project from construction through commissioning. Contact us today for a free consultation.
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        <Link
                            to="/contact"
                            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-blue-600 rounded-xl font-semibold hover:shadow-lg transition-all duration-300"
                        >
                            Get a Free Consultation
                            <FaArrowRight size={14} />
                        </Link>
                        <a
                            href="tel:+12014099845"
                            className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white rounded-xl font-semibold hover:bg-white/10 transition-all duration-300"
                        >
                            <FaPhone size={14} />
                            Call Us Now
                        </a>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default DataCenterCommissioningPage;
